import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Employee } from '../../types';
import './PositionCell.css';

interface PositionCellProps {
  row: Employee;
  options: readonly string[];
  onPositionChange: (id: number, position: string) => void;
  disabled?: boolean;
}

const PositionCell: React.FC<PositionCellProps> = ({ row, options, onPositionChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  const handleSelect = useCallback(
    (position: string) => {
      if (position !== row.position) {
        onPositionChange(row.id, position);
      }
      setIsOpen(false);
    },
    [row.id, row.position, onPositionChange]
  );

  // Position the dropdown when it opens
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 180)
      });
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  return (
    <div className="position-cell">
      <button
        ref={triggerRef}
        type="button"
        className="position-cell-trigger"
        onClick={handleToggle}
        title={disabled ? row.position : 'Click to change position'}
        disabled={disabled}
        style={disabled ? { cursor: 'default', opacity: 1 } : undefined}
      >
        <span className="position-cell-value">{row.position || (disabled ? '' : 'Select position')}</span>
        {!disabled && <span className="position-cell-chevron">{isOpen ? '▲' : '▼'}</span>}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="position-dropdown"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width
            }}
          >
            {options.map(option => (
              <button
                key={option}
                type="button"
                className={`position-option ${option === row.position ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {option}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default PositionCell;

